import coverBg from "@/assets/textures/tournament-rewards-block.png"
import TonIcon from "@/assets/icons/ton-icon.svg?react"
import { useI18n } from "@/shared/i18n"
import { formatTonFromNano } from "@/shared/lib/pvpRouletteMath"
import { cn } from "@/shared/lib/utils"
import type { TournamentSummary } from "@/shared/api/types/tournaments"

import { openTournamentNominationsPost } from "../openTournamentNominationsPost"
import { TournamentFlipCountdown } from "./TournamentFlipCountdown"

/** Render text with `**...**` segments highlighted in green (#00FE74). */
function renderHighlighted(text: string) {
  return text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith("**") && part.endsWith("**") ? (
      <span key={i} className="text-green-100">
        {part.slice(2, -2)}
      </span>
    ) : (
      part
    )
  )
}

type TournamentPhase = "upcoming" | "active" | "finished"

function resolvePhase(tournament: TournamentSummary, now: number): TournamentPhase {
  if (tournament.isFinished || now >= tournament.endsAt) return "finished"
  if (tournament.isUpcoming || now < tournament.startsAt) return "upcoming"
  return "active"
}

interface TournamentRewardCardProps {
  tournament: TournamentSummary
  onViewLeaderboard?: () => void
  className?: string
}

/**
 * Tournament card for the Rewards screen. Three phases:
 *   - upcoming → cover countdown to start; no prize pool / leaderboard yet;
 *   - active   → cover countdown to end + live prize pool + top players + CTA;
 *   - finished → same layout, countdown frozen at zero, final pool.
 *
 * NOTE: the contract carries `current_pool_ton_equiv` + `prize_places_count`
 * but no per-tier prize breakdown, so the "top players" reward currently shows
 * the full pool. The "bottom players" tier from the mock is intentionally
 * omitted (no such concept in the data). Texts/UI to be refined.
 */
export function TournamentRewardCard({
  tournament,
  onViewLeaderboard,
  className,
}: TournamentRewardCardProps) {
  const { t } = useI18n()
  const phase = resolvePhase(tournament, Date.now())
  const isUpcoming = phase === "upcoming"

  const coverTarget = isUpcoming ? tournament.startsAt : tournament.endsAt
  const coverLabel = isUpcoming
    ? t("tournamentRewards.startsIn")
    : phase === "finished"
      ? t("tournamentRewards.ended")
      : t("tournamentRewards.endsIn")

  const pool = formatTonFromNano(tournament.currentPoolNanoTon, {
    allowZero: true,
  })

  return (
    <div
      className={cn(
        "flex flex-col gap-4 rounded-[20px] bg-secondary-main p-2",
        className
      )}
    >
      {/* Cover */}
      <div
        className="relative flex h-28 flex-col justify-end gap-1.5 overflow-hidden rounded-2xl p-3"
        style={{
          backgroundImage: `url(${coverBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <span className="text-[10px] font-semibold leading-3 tracking-[-0.5px] text-green-100">
          {coverLabel}
        </span>
        <TournamentFlipCountdown to={coverTarget} variant="lg" accentLastUnit />
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-3 rounded-xl bg-tertiary-main p-3">
          <p className="flex-1 text-sm leading-[1.3] tracking-[-0.5px] text-white">
            {renderHighlighted(t("tournamentRewards.rules"))}
          </p>
          <div className="flex shrink-0 flex-col items-start">
            <span className="flex items-center gap-1 text-xs leading-4 tracking-[-0.5px] text-white">
              {phase === "finished"
                ? t("tournamentRewards.prizePool")
                : t("tournamentRewards.livePrizePool")}
              {phase !== "finished" && (
                <span className="size-1.5 rounded-full bg-green-100 shadow-[0_0_7px_#00FE74]" />
              )}
            </span>
            <span className="flex items-center gap-0.5 text-[32px] font-bold leading-[39px] tracking-[-1.5px] text-white">
              {pool}
              <TonIcon className="size-6" />
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={openTournamentNominationsPost}
            className="flex h-10 flex-1 items-center justify-center rounded-xl bg-white text-sm font-semibold tracking-[-0.5px] text-black-100 transition-opacity active:opacity-80"
          >
            {t("tournamentRewards.checkNominations")}
          </button>
          <button
            type="button"
            onClick={onViewLeaderboard}
            className="flex h-10 flex-1 items-center justify-center rounded-xl bg-green-100 text-sm font-semibold tracking-[-0.5px] text-black-100 transition-opacity active:opacity-80"
          >
            {t("tournamentRewards.viewLeaderboard")}
          </button>
        </div>
      </div>
    </div>
  )
}
